import { useCallback, useEffect, useState, useMemo } from "react";
import useDiarySearch from "../data/diary/useDiarySearch";
import useShowNoResults from "../ui/useShowNoResults";

import { getDiaryLoadmore } from "../../api/diary-service";

const EMPTY_RESULT = {
    user_items: [],
    items: [],
    favorite: [],
    user_recipes: [],
    recipes: [],
};

const useSearchState = () => {
    const [searchInput, setSearchInput] = useState("");

    const [checkboxes, setCheckboxes] = useState({
        db1: true,
        db2: true,
    });

    const [expandedCategories, setExpandedCategories] = useState({});
    const [searchMode, setSearchMode] = useState(false);

    const {
        data: searchResult,
        isLoading,
        isFetching,
        isError,
        error,
        enabled,
        debouncedQuery,
    } = useDiarySearch(searchInput, checkboxes);

    const hasQuery = searchInput.trim().length > 0;

    // Normalize search result
    const data = hasQuery ? (searchResult ?? EMPTY_RESULT) : EMPTY_RESULT;

    const safeArr = (arr) => (Array.isArray(arr) ? arr : []);

    const buildItemsState = (source, type) => {

        return {
            data: safeArr(source[type].data),
            paginationDetails: {
                total: source[type].total,
                per_page: source[type].per_page,
                current_page: source[type].current_page,
                last_page: source[type].last_page,
            },
        }
    };

    const [user_items, setUserItems] = useState(() => buildItemsState(data, 'user_items'));
    const [items, setItems] = useState(() => buildItemsState(data, 'items'));
    const [recipes, setRecipes] = useState(() => buildItemsState(data, 'recipes'));
    const [user_recipes, setUserRecipes] = useState(() => buildItemsState(data, 'user_recipes'));
    const [favorite, setFavorite] = useState(() => buildItemsState(data, 'favorite'));

    useEffect(() => {
        setUserItems(buildItemsState(data, 'user_items'));
        setItems(buildItemsState(data, 'items'));
        setRecipes(buildItemsState(data, 'recipes'));
        setUserRecipes(buildItemsState(data, 'user_recipes'));
        setFavorite(buildItemsState(data, 'favorite'));
    }, [searchResult, hasQuery, checkboxes.db1, checkboxes.db2]);

    const showNoResults = useShowNoResults({
        searchInput,
        debouncedQuery,
        enabled,
        isFetching,
        isLoading,
        searchResult: data,
    });

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchInput(value);
        if (value.trim().length > 0) setSearchMode(true);
    }, []);

    const handleExpandCategory = useCallback(
        (title) =>
            setExpandedCategories((prev) => ({
                ...prev,
                [title]: !prev[title],
            })),
        [],
    );

    const resetSearch = useCallback(() => {
        setSearchInput("");
        setSearchMode(false);
        setExpandedCategories({});
    }, []);

    const clearSearchInput = useCallback(() => {
        setSearchInput("");
    }, []);


    const setters = useMemo(() => ({
        user_items: setUserItems,
        recipes: setRecipes,
        items: setItems,
        user_recipes: setUserRecipes,
        favorite: setFavorite
    }), []);

    const appendPaginationData = (prev, response) => ({
        data: [
            ...prev.data,
            ...safeArr(response?.data?.data)
        ],
        paginationDetails: {
            ...prev.paginationDetails,
            ...(response?.data?.total != null && {
                total: response.data.total
            }),
            ...(response?.data?.per_page != null && {
                per_page: response.data.per_page
            }),
            ...(response?.data?.current_page != null && {
                current_page: response.data.current_page
            }),
            ...(response?.data?.last_page != null && {
                last_page: response.data.last_page
            }),
        }
    });

    const loadMore = useCallback(async (incoming) => {

        const res = await getDiaryLoadmore({ ...incoming, query: searchInput, ...checkboxes });

        const setter = setters[incoming.type];

        if (!setter) return;

        setter(prev => appendPaginationData(prev, res));

    }, [checkboxes, searchInput, setters]);

    return {
        searchInput,
        expandedCategories,
        showNoResults,
        items,
        user_items,
        favorite,
        user_recipes,
        recipes,
        isLoading,
        isError,
        error,
        searchMode,
        checkboxes,
        onSearchChange: handleSearchChange,
        onExpandCategory: handleExpandCategory,
        onResetSearch: resetSearch,
        onClearSearchInput: clearSearchInput,
        onSelectCheckboxes: setCheckboxes,
        onLoadMore: loadMore
    };
};

export default useSearchState;
